const IMAGE_WIDTH = 1672;
const IMAGE_HEIGHT = 941;
/** Idle / teaser anchor — upper chest, below face */
const FACE_CENTER = { x: 0.5, y: 0.57 };
const LERP = 0.28;
const RADIUS_FACTOR = 0.16;
const SOFTNESS_FACTOR = 1.1;
const CELL_LERP = 0.2;

function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export function initHeroReveal() {
  const section = document.querySelector('.hero-splash#home, #home.hero-splash, #home');
  const media = document.querySelector('.hero-splash__media');
  const realImg = media?.querySelector('.hero-splash__img--reveal-real');
  const darkImg = media?.querySelector('.hero-splash__img--dark');
  const pixelCanvas =
    document.getElementById('hero-reveal-pixel') ||
    document.getElementById('hero-reveal-ink') ||
    document.getElementById('hero-reveal-smoke');
  const cursorDot = document.getElementById('hero-reveal-cursor');
  if (!section || !media || !realImg || !pixelCanvas) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobileMq = window.matchMedia('(max-width: 768px)');

  let instance = null;
  let pendingInit = false;
  let themeObserver = null;

  function isLightTheme() {
    return document.body.classList.contains('light-theme');
  }

  function isMobile() {
    return mobileMq.matches;
  }

  function isRevealEnabled() {
    return !prefersReducedMotion && !isLightTheme() && !isMobile();
  }

  function getPointerClientCoords(e) {
    const custom = document.querySelector('.custom-cursor');
    if (custom && window.innerWidth > 768) {
      const left = parseFloat(custom.style.left);
      const top = parseFloat(custom.style.top);
      if (Number.isFinite(left) && Number.isFinite(top)) {
        return { x: left, y: top };
      }
    }
    return { x: e.clientX, y: e.clientY };
  }

  function pointerToMediaPercent(clientX, clientY, rect) {
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  }

  function defaultBrushRadius(rect) {
    return Math.min(rect.width, rect.height) * RADIUS_FACTOR;
  }

  function parseObjectPosition(objectPosition) {
    const keywords = { left: 0, right: 1, top: 0, bottom: 1, center: 0.5 };
    const horiz = new Set(['left', 'right', 'center']);
    const vert = new Set(['top', 'bottom', 'center']);
    const tokenToAxis = (token) => {
      if (!token) return 0.5;
      if (token in keywords) return keywords[token];
      if (token.endsWith('%')) return Math.max(0, Math.min(1, parseFloat(token) / 100));
      return 0.5;
    };

    const tokens = objectPosition.trim().split(/\s+/);
    if (tokens.length === 1) {
      const t = tokens[0];
      if (vert.has(t)) return { x: 0.5, y: tokenToAxis(t) };
      return { x: tokenToAxis(t), y: 0.5 };
    }

    let t0 = tokens[0];
    let t1 = tokens[1];
    if (vert.has(t0) && horiz.has(t1)) {
      const swap = t0;
      t0 = t1;
      t1 = swap;
    }
    return { x: tokenToAxis(t0), y: tokenToAxis(t1) };
  }

  /** object-fit: cover — crop matches live CSS on the dark hero img */
  function computeCoverSource(width, height) {
    let posX = 0.5;
    let posY = 0;
    if (darkImg) {
      const parsed = parseObjectPosition(getComputedStyle(darkImg).objectPosition);
      posX = parsed.x;
      posY = parsed.y;
    }

    const scale = Math.max(width / IMAGE_WIDTH, height / IMAGE_HEIGHT);
    const renderedW = IMAGE_WIDTH * scale;
    const renderedH = IMAGE_HEIGHT * scale;
    const cropX = Math.max(0, renderedW - width);
    const cropY = Math.max(0, renderedH - height);
    const offsetX = cropX * posX;
    const offsetY = cropY * posY;

    return {
      sx: offsetX / scale,
      sy: offsetY / scale,
      sw: width / scale,
      sh: height / scale,
    };
  }

  function pixelSizeFor(width) {
    if (width < 420) return 6;
    if (width < 900) return 7;
    return 8;
  }

  function createRevealInstance() {
    let rafId = 0;
    let resizeObserver = null;
    let running = false;
    let ctx = null;
    let cols = 0;
    let rows = 0;
    let pixelSize = 12;
    let cellReveal = null;
    let cellThreshold = null;
    let cover = computeCoverSource(1, 1);

    const state = {
      mouseTarget: { xPct: 50, yPct: 57 },
      mouseCurrent: { xPct: 50, yPct: 57 },
      radiusTarget: 0,
      radiusCurrent: 0,
      softnessTarget: 48,
      softnessCurrent: 48,
      mixTarget: 0,
      mixCurrent: 0,
      pointerInside: false,
      touchActive: false,
      teaserPhase: 0,
      lastMoveTime: 0,
      idlePulse: 0,
    };

    function initGrid(width, height) {
      pixelSize = pixelSizeFor(width);
      cols = Math.max(1, Math.ceil(width / pixelSize));
      rows = Math.max(1, Math.ceil(height / pixelSize));
      const n = cols * rows;
      cellReveal = new Float32Array(n);
      cellThreshold = new Float32Array(n);
      for (let i = 0; i < n; i += 1) {
        cellThreshold[i] = Math.random();
      }
      cover = computeCoverSource(width, height);
    }

    function resizeCanvas() {
      const rect = media.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      pixelCanvas.width = w * dpr;
      pixelCanvas.height = h * dpr;
      pixelCanvas.style.width = `${w}px`;
      pixelCanvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initGrid(w, h);
    }

    function updateCells(width, height) {
      if (!cellReveal) return;

      const px = (state.mouseCurrent.xPct / 100) * width;
      const py = (state.mouseCurrent.yPct / 100) * height;
      const boost = 1 + state.mixCurrent * 0.9;
      const r = state.radiusCurrent * boost;
      const soft = Math.max(12, state.softnessCurrent);
      const outer = r + soft;

      if (r < 2) {
        const fadeOut = 0.14;
        for (let i = 0; i < cellReveal.length; i += 1) {
          cellReveal[i] += (0 - cellReveal[i]) * fadeOut;
        }
        return;
      }

      const inner = r * 0.45;
      const stagger = 0.28 + state.mixCurrent * 0.22;

      let idx = 0;
      for (let row = 0; row < rows; row += 1) {
        const cy = (row + 0.5) * pixelSize;
        for (let col = 0; col < cols; col += 1) {
          const cx = (col + 0.5) * pixelSize;
          const d = Math.hypot(cx - px, cy - py);
          let target = 0;

          if (d < outer) {
            const falloff = 1 - smoothstep(inner, outer, d);
            const gate = cellThreshold[idx] * stagger;
            target = Math.max(0, Math.min(1, (falloff - gate) / (1 - gate + 0.001)));
            target = target * target * (3 - 2 * target);
          }

          cellReveal[idx] += (target - cellReveal[idx]) * CELL_LERP;
          idx += 1;
        }
      }
    }

    function drawPixelDissolve(width, height) {
      if (!ctx || !cellReveal) return;

      ctx.clearRect(0, 0, width, height);
      ctx.imageSmoothingEnabled = false;

      const { sx, sy, sw, sh } = cover;
      let idx = 0;

      for (let row = 0; row < rows; row += 1) {
        const dy = row * pixelSize;
        const dh = Math.min(pixelSize, height - dy);
        const syCell = sy + (dy / height) * sh;
        const shCell = (dh / height) * sh;

        for (let col = 0; col < cols; col += 1) {
          const alpha = cellReveal[idx];
          idx += 1;
          if (alpha < 0.02) continue;

          const dx = col * pixelSize;
          const dw = Math.min(pixelSize, width - dx);
          const sxCell = sx + (dx / width) * sw;
          const swCell = (dw / width) * sw;

          ctx.globalAlpha = alpha;
          ctx.drawImage(realImg, sxCell, syCell, swCell, shCell, dx, dy, dw, dh);
        }
      }

      ctx.globalAlpha = 1;
    }

    function tick(time) {
      if (!running) return;

      state.mouseCurrent.xPct += (state.mouseTarget.xPct - state.mouseCurrent.xPct) * LERP;
      state.mouseCurrent.yPct += (state.mouseTarget.yPct - state.mouseCurrent.yPct) * LERP;
      state.radiusCurrent += (state.radiusTarget - state.radiusCurrent) * LERP;
      state.softnessCurrent += (state.softnessTarget - state.softnessCurrent) * LERP;
      state.mixCurrent += (state.mixTarget - state.mixCurrent) * LERP;

      const rect = media.getBoundingClientRect();
      const mw = Math.max(1, rect.width);
      const mh = Math.max(1, rect.height);
      const baseR = defaultBrushRadius(rect);

      if (state.pointerInside && performance.now() - state.lastMoveTime > 400) {
          state.idlePulse += 0.025;
          const pulse = (Math.sin(state.idlePulse) + 1) * 0.5;
          state.radiusTarget = Math.max(state.radiusTarget, baseR * (0.9 + pulse * 0.12));
          state.mouseTarget.xPct = FACE_CENTER.x * 100 + Math.sin(state.idlePulse * 0.6) * 1.5;
          state.mouseTarget.yPct = FACE_CENTER.y * 100 + Math.cos(state.idlePulse * 0.5) * 1.2;
        }

      updateCells(mw, mh);
      drawPixelDissolve(mw, mh);

      const cursorEl = cursorDot || document.documentElement;
      if (state.pointerInside && state.radiusCurrent > 4) {
        const pxX = (state.mouseCurrent.xPct / 100) * mw;
        const pxY = (state.mouseCurrent.yPct / 100) * mh;
        cursorEl.style.setProperty('--hero-reveal-cursor-x', `${rect.left + pxX}px`);
        cursorEl.style.setProperty('--hero-reveal-cursor-y', `${rect.top + pxY}px`);
        cursorEl.style.setProperty('--hero-reveal-pixel-size', `${pixelSize}px`);
        cursorEl.style.setProperty(
          '--hero-reveal-brush-size',
          `${Math.max(pixelSize * 4, Math.min(120, state.radiusCurrent * 1.2))}px`
        );
        cursorEl.style.setProperty('--hero-reveal-cursor-visible', '1');
      } else {
        cursorEl.style.setProperty('--hero-reveal-cursor-visible', '0');
      }

      rafId = requestAnimationFrame(tick);
    }

    function pointerInHero(clientX, clientY) {
      const rect = section.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    }

    function applyPointerAt(clientX, clientY) {
      const rect = media.getBoundingClientRect();
      const pct = pointerToMediaPercent(clientX, clientY, rect);
      const brush = defaultBrushRadius(rect);
      state.mouseTarget.xPct = pct.x;
      state.mouseTarget.yPct = pct.y;
      state.radiusTarget = brush;
      state.softnessTarget = brush * SOFTNESS_FACTOR;
      state.lastMoveTime = performance.now();
      state.pointerInside = true;
    }

    function onPointerMove(e) {
      const coords = getPointerClientCoords(e);
      if (!pointerInHero(coords.x, coords.y)) {
        if (state.pointerInside) onPointerLeave();
        return;
      }
      applyPointerAt(coords.x, coords.y);
    }

    function onPointerLeave() {
      state.pointerInside = false;
      state.radiusTarget = 0;
      state.mixTarget = 0;
      state.mouseTarget.xPct = FACE_CENTER.x * 100;
      state.mouseTarget.yPct = FACE_CENTER.y * 100;
    }

    const captureOpts = { capture: true, passive: true };

    function bindEvents() {
      document.addEventListener('pointermove', onPointerMove, captureOpts);

      resizeObserver = new ResizeObserver(() => resizeCanvas());
      resizeObserver.observe(media);
      window.addEventListener('pagehide', dispose, { once: true });
    }

    function unbindEvents() {
      document.removeEventListener('pointermove', onPointerMove, captureOpts);
      resizeObserver?.disconnect();
      resizeObserver = null;
    }

    function dispose() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      unbindEvents();
      section.classList.remove('is-reveal-active');
      media.classList.remove(
        'is-reveal-active',
        'is-reveal-pixel',
        'is-reveal-hint-dismissed'
      );
      document.body.classList.remove('hero-reveal-active');
      realImg.style.maskImage = '';
      realImg.style.webkitMaskImage = '';
      if (ctx) {
        const rect = media.getBoundingClientRect();
        ctx.clearRect(0, 0, rect.width, rect.height);
      }
      pixelCanvas.classList.remove('is-active');
      if (cursorDot) {
        cursorDot.style.setProperty('--hero-reveal-cursor-visible', '0');
      }
      instance = null;
    }

    function start() {
      return new Promise((resolve, reject) => {
        if (realImg.complete && realImg.naturalWidth > 0) {
          onReady();
        } else {
          realImg.addEventListener('load', onReady, { once: true });
          realImg.addEventListener(
            'error',
            () => reject(new Error('LIL8 failed to load')),
            { once: true }
          );
        }

        function onReady() {
          ctx = pixelCanvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas 2D unavailable'));
            return;
          }

          resizeCanvas();
          pixelCanvas.classList.add('is-active');
          section.classList.add('is-reveal-active');
          media.classList.add('is-reveal-active', 'is-reveal-pixel');
          document.body.classList.add('hero-reveal-active');
          state.mouseCurrent.xPct = FACE_CENTER.x * 100;
          state.mouseCurrent.yPct = FACE_CENTER.y * 100;
          state.mouseTarget.xPct = FACE_CENTER.x * 100;
          state.mouseTarget.yPct = FACE_CENTER.y * 100;

          bindEvents();
          running = true;
          rafId = requestAnimationFrame(tick);
          instance = { dispose };
          resolve();
        }
      });
    }

    return { start, dispose };
  }

  function teardown() {
    instance?.dispose?.();
    instance = null;
    pendingInit = false;
  }

  async function tryInit() {
    if (!isRevealEnabled()) {
      teardown();
      return;
    }
    if (instance || pendingInit) return;

    pendingInit = true;
    media.removeAttribute('data-reveal-error');

    try {
      const reveal = createRevealInstance();
      await reveal.start();
    } catch (err) {
      console.warn('[hero-reveal] init failed:', err);
      media.setAttribute('data-reveal-error', err?.message || 'init failed');
      teardown();
    } finally {
      pendingInit = false;
    }
  }

  function watchTheme() {
    if (themeObserver) return;
    themeObserver = new MutationObserver(() => {
      if (isRevealEnabled()) tryInit();
      else teardown();
    });
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  function boot() {
    if (!isRevealEnabled()) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => tryInit());
    });
  }

  watchTheme();
  boot();

  mobileMq.addEventListener('change', () => {
    teardown();
    boot();
  });

  return { teardown, tryInit };
}
